import { useState, useEffect } from 'react';
import { Box, Container, Stepper, Step, StepLabel, Button, Link } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

import ListingOnboardingStep0 from './ListingOnboardingStep0';
import ListingOnboardingStep2 from './ListingOnboardingStep2';
import ListingOnboardingStep1 from './ListingOnboardingStep1';
import ListingOnboardingStep3 from './ListingOnboardingStep3';

import GoogleSignInButton from '../../components/GoogleSignInButton';
import { PrimaryButton, SecondaryButton, } from '../../components';
import { GoogleAuthService } from '../../services/GoogleAuthService';
import { listingService } from '../../services/ListingService';
import axiosClient from '../../services/axiosClient';

import AnimatedIntro from '../../components/AnimatedIntro'

import { ThemeProvider } from '@mui/material/styles';
import theme from '../../styles/theme'

import { ListingFormData } from '../../types/listing_onboarding'
import ListingProcessingStep from '../../components/ListingProcessingStep';
import logo from '../../assets/rounded_logo.svg';

interface ListingOnboardingFlowProps {
  isOverlay?: boolean;
  listingId?: string;
  aiEmail?: string;
  pendingListingId?: string;
}

const defaultFormData: ListingFormData = {
  platforms: {
    zillow: false,
    facebook: false,
    apartments: false,
    craigslist: false
  },
  lease_types: {
    fixed_term_lease: true,
    month_to_month: false,
    sublease: false
  },
  pet_policy: {
    no_pets_allowed: true,
    cats_allowed: false,
    small_dogs_allowed: false,
    large_dogs_allowed: false,
  },
  full_address: '',
  google_place_id: '',
  date_available: new Date().toISOString().split('T')[0],
  rent: '',
  security_deposit: '',
  title: '',
  description: '',
  num_bedrooms: '1',
  num_bathrooms: '1',
  rental_type: '',
  has_private_bathroom: true,
  shared_bathroom_count: '',
  laundry_type: 'in_unit',
  unit_occupied: false,
  current_tenant_contact: '',
  application_url: 'apply.heyyproperty.com',
  group_showings: true,
};

const GOOGLE_MAPS_SRC = "https://maps.googleapis.com/maps/api/js?key=AIzaSyDlBkUlEkh35iQx8z7C5bzUbU556yGg8qw&loading=async&libraries=places";

function loadGoogleMapsScript() {
  if (document.querySelector(`script[src="${GOOGLE_MAPS_SRC}"]`)) return; // Prevent duplicate
  const script = document.createElement('script');
  script.src = GOOGLE_MAPS_SRC;
  script.async = true;
  document.body.appendChild(script);
}

const ListingOnboardingFlow: React.FC<ListingOnboardingFlowProps> = ({
  isOverlay = false,
  listingId,
  aiEmail,
  pendingListingId
}) => {
  const [isStepValid, setIsStepValid] = useState(true);
  const [listingCredit, setListingCredit] = useState<number | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string | 'pending' | 'success' | 'failed' | null>(null);

  // Photo management state
  const [photos, setPhotos] = useState<any[]>([]);
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false);

  // Initialize form data from localStorage or use defaults
  const [formData, setFormData] = useState<ListingFormData>(() => {
    try {
      const savedFormData = localStorage.getItem('listingOnboardingFormData');
      if (savedFormData) {
        // Parse the saved data
        const parsedData = JSON.parse(savedFormData);

        // Return a merged object that ensures any new fields in defaultFormData are included
        return { ...defaultFormData, ...parsedData };
      }
    } catch (error) {
      console.error('Error loading saved form data:', error);
    }

    // If no saved data or error, return default
    return defaultFormData;
  });

  // Initialize current step from localStorage or default to 0
  const [currentStep, setCurrentStep] = useState<number>(() => {
    try {
      const savedStep = localStorage.getItem('listingOnboardingStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
    } catch (error) {
      console.error('Error loading saved step:', error);
      return 0;
    }
  });

  const [responseListingId, setResponseListingId] = useState<string | undefined>(() => {
    // Try to get from localStorage first, then fall back to props
    const storedListingId = localStorage.getItem('listing-id');
    return storedListingId || listingId;
  });
  const [responseAiEmail, setResponseAiEmail] = useState<string | undefined>(() => {
    // Try to get from localStorage first, then fall back to props
    const storedAiEmail = localStorage.getItem('ai-email');
    return storedAiEmail || aiEmail;
  });

  const [showAnimation, setShowAnimation] = useState(() => {
    // Check if we've shown the animation before
    return !isOverlay && localStorage.getItem('listingAnimationShown') !== 'true';
  });

  const [processingStatus, setProcessingStatus] = useState<'processing' | 'uploading' | 'success' | 'error'>(() => {
    return localStorage.getItem('listing-create-processing') as 'processing' | 'uploading' | 'success' | 'error' || 'processing';
  });

  // Load photos from pending listing on mount
  useEffect(() => {
    const loadPhotos = async () => {
      if (!pendingListingId) return;

      setIsLoadingPhotos(true);
      try {
        const pendingPhotos = await listingService.getPendingListingPhotos(pendingListingId);
        setPhotos(pendingPhotos);
      } catch (error) {
        console.error('Error loading photos from pending listing:', error);
      } finally {
        setIsLoadingPhotos(false);
      }
    };

    loadPhotos();
  }, [pendingListingId]);

  // Save pending listing ID to localStorage
  useEffect(() => {
    if (pendingListingId) {
      localStorage.setItem('pending-listing-id', pendingListingId);
    }
  }, [pendingListingId]);

  // Save listingId to localStorage when it changes
  useEffect(() => {
    if (listingId && listingId !== '' && !localStorage.getItem('listing-id')) {
      localStorage.setItem('listing-id', listingId);
    }
  }, [listingId]);

  useEffect(() => {
    if (aiEmail && aiEmail !== '') {
      localStorage.setItem('ai-email', aiEmail);
    }
  }, [aiEmail]);

  // Handle animation completion
  const handleAnimationComplete = () => {
    setShowAnimation(false);
    localStorage.setItem('listingAnimationShown', 'true');
  };

  // Save form data to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('listingOnboardingFormData', JSON.stringify(formData));
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  }, [formData]);

  // Save current step to localStorage whenever it changes
  useEffect(() => {
    if (currentStep === 4) {
      setPaymentStatus(null);
    }
    try {
      localStorage.setItem('listingOnboardingStep', currentStep.toString());
    } catch (error) {
      console.error('Error saving current step:', error);
    }
  }, [currentStep]);

  const { isAuthenticated: initialIsAuthenticated } = useAuth();
  const [isAuthenticated, setIsAuthenticated] = useState(initialIsAuthenticated);

  // Add useEffect to fetch listing credit when needed
  useEffect(() => {
    const fetchListingCredit = async () => {
      if (currentStep === 3 && isAuthenticated) {
        try {
          const creditResponse = await axiosClient.get('/listings/get_listing_credit');
          setListingCredit(creditResponse.data.listing_credit);
        } catch (error) {
          console.error('Error fetching listing credit:', error);
        }
      }
    };

    fetchListingCredit();

    // If we have a payment session_id in the URL, start polling for updates
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const failedPayment = urlParams.get('failed') === 'true';

    if (failedPayment) {
      setPaymentStatus('failed');
    }

    if (sessionId) {
      const pollInterval = setInterval(async () => {
        try {
          const creditResponse = await axiosClient.get('/listings/get_listing_credit');
          const newCredit = creditResponse.data.listing_credit;

          if (newCredit > (listingCredit || 0)) {
            setPaymentStatus('success');
            setListingCredit(newCredit);
            clearInterval(pollInterval); // Stop polling once we see the credit increase
          }
        } catch (error) {
          console.error('Error polling for listing credit:', error);
        }
      }, 1000); // Poll every 1 second

      // Stop polling after 10 seconds (webhook should have processed by then)
      setTimeout(() => clearInterval(pollInterval), 10000);

      return () => clearInterval(pollInterval);
    }
  }, [currentStep, isAuthenticated]);

  const generateAIContent = async () => {
    // Set flag to indicate AI generation is in progress
    sessionStorage.setItem('ai_generation_in_progress', 'true');

    try {
      const response = await axiosClient.post('/api/v1/open_ai_api/generate_details', {
        full_address: formData.full_address,
        rent: formData.rent,
        rental_type: formData.rental_type,
        num_bedrooms: formData.num_bedrooms,
        num_bathrooms: formData.num_bathrooms,
        laundry_type: formData.laundry_type,
        shared_bathroom_count: formData.shared_bathroom_count,
        has_private_bathroom: formData.has_private_bathroom,
        female_roommates: formData.female_count,
        male_roommates: formData.male_count,
        month_to_month_lease_available: formData.lease_types.month_to_month,
        sublease_available: formData.lease_types.sublease,
        fixed_lease_available: formData.lease_types.fixed_term_lease,
        date_available: formData.date_available,
        pet_policy: formData.pet_policy,
        property_type: formData.property_type,
      });

      const data = response.data;
      sessionStorage.setItem('ai_generated_content', JSON.stringify({
        title: data.title,
        description: data.description,
      }));

      // Clear the generation flag since we're done
      sessionStorage.removeItem('ai_generation_in_progress');
    } catch (error) {
      console.error('Error fetching property details:', error);
      //sessionStorage.setItem('ai_listing_failure', 'true');
      // Clear the generation flag on error
      sessionStorage.removeItem('ai_generation_in_progress');
    } finally {
    }
  };


  const handleNext = () => {
    window.scrollTo(0, 0);
    if (currentStep === 0 && !formData.title && !formData.description) {
      generateAIContent()
    } else if (currentStep === 3 && isAuthenticated) {
      handleSubmit()
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    window.scrollTo(0, 0);
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    const pending_listing_id = pendingListingId || localStorage.getItem('pending-listing-id');
    if (!pending_listing_id) {
      console.error('No pending listing ID found');
      alert('Something went wrong. Please contact support at rich@heyyproperty.com and tell him you are creating a listing and  pending_listing_id not found.');
      return;
    }
    setPaymentStatus(null);
    setProcessingStatus('processing');
    localStorage.setItem('listing-create-processing', 'processing');

    listingService.create(formData, pending_listing_id, {
      onSuccess: (data) => {
        // Store the response data
        setResponseListingId(data.listingId);
        setResponseAiEmail(data.aiEmail);

        // Save to localStorage
        if (data.listingId) {
          localStorage.setItem('listing-id', data.listingId);
        }
        if (data.aiEmail) {
          localStorage.setItem('ai-email', data.aiEmail);
        }

        setProcessingStatus('success');
        localStorage.setItem('listing-create-processing', 'success');
      },
      onError: (error) => {
        console.error('Error creating listing:', error);
        setProcessingStatus('error');
        localStorage.setItem('listing-create-processing', 'error');
      }
    });
  };

  const renderActionButton = () => {
    // Show GoogleSignInButton on last step if not logged in
    if (currentStep === 4 && isAuthenticated) {
      return <></>
    }
    if (currentStep === 3 && !isAuthenticated) {
      return (
        <Box
        sx={{ flex: 3,height: '36px',backgroundColor: 'transparent',borderRadius: '9999px',
          minWidth: '180px',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2
        }}
          >
        <GoogleSignInButton
        googleCallback={(response) =>
          GoogleAuthService.handleGoogleResponse(response, async () => {
            setIsAuthenticated(true);
            try {
              const creditResponse = await axiosClient.get('/listings/get_listing_credit');
              setListingCredit(creditResponse.data.listing_credit);
            } catch (error) {
              console.error('Error fetching listing credit:', error);
            }
          })}
        />
        </Box>
      );
    }
    else if (currentStep === 3 && isAuthenticated && listingCredit != null && listingCredit === 0 && !localStorage.getItem("listing-create-processing")) {
      return (
        <PrimaryButton
          variant="outlined"
          sx={{
            width: '100%',
            flex: 3,
          }}
          onClick={async () => {
            try {
              setPaymentStatus('pending');
              const response = await axiosClient.post('/payments/create_checkout_session', {
                source: 'onboarding'
              });
              window.open(response.data.url, '_blank', 'noopener,noreferrer');
            } catch (error) {
              console.error('Error creating checkout session:', error);
            }
          }}
        >
          Proceed to Payment
        </PrimaryButton>
      );
    }

    return (
      <PrimaryButton
        variant="outlined"
        onClick={handleNext}
        disabled={!isStepValid || currentStep >= 4}
        sx={{
          flex: currentStep === 0 ? 1 : 3,
        }}
      >
        {(() => {
          switch (currentStep) {
            case 0:
              return 'Select Photos ->';
            case 1:
              return formData.title || formData.description
                ? 'DESCRIPTION ->'
                : 'CREATE DESCRIPTION ✨';
            case 3:
              return 'SUBMIT';
            case 4:
              return 'DONE';
            default:
              return 'NEXT ->';
          }
        })()}
      </PrimaryButton>
    );
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <ListingOnboardingStep0
            onNext={handleNext}
            formData={formData}
            setFormData={setFormData}
            setStepValid={setIsStepValid}
          />
        );
      case 1:
        return (
          <ListingOnboardingStep1
            onNext={handleNext}
            onBack={handleBack}
            formData={formData}
            setFormData={setFormData}
            setStepValid={setIsStepValid}
            pendingListingId={pendingListingId}
            photos={photos}
            setPhotos={setPhotos}
            isLoadingPhotos={isLoadingPhotos}
          />
        );
      case 2:
        return (
          <ListingOnboardingStep2
            onNext={handleNext}
            onBack={handleBack}
            formData={formData}
            setFormData={setFormData}
            setStepValid={setIsStepValid}

          />
        );
      case 3:
        return (
          <ListingOnboardingStep3
            onSumbit={handleSubmit}
            onBack={handleBack}
            formData={formData}
            setFormData={setFormData}
            setStepValid={setIsStepValid}
            listingCredit={listingCredit}
            paymentStatus={paymentStatus}
          />
        );
      case 4:
        return (
          <ListingProcessingStep
            status={processingStatus}
            listingId={responseListingId || listingId}
            aiEmail={responseAiEmail || aiEmail}
            onBack={handleBack}
          />
        );
      // Add other steps here
      default:
        return null;
    }
  };

  useEffect(() => {
    loadGoogleMapsScript();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  // Cleanup AI generation flag when component unmounts
  useEffect(() => {
    return () => {
      sessionStorage.removeItem('ai_generation_in_progress');
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ pb: '140px' }}>
        {showAnimation && (
          <AnimatedIntro onComplete={handleAnimationComplete} />
        )}
        {!isOverlay && (
          <Box sx={{
            backgroundColor: 'white',
            position: isOverlay ? 'static' : 'fixed',
            top: isOverlay ? undefined : 0,
            left: isOverlay ? undefined : 0,
            right: isOverlay ? undefined : 0,
            padding: '0 0 10px 0',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            mt: 0,
            zIndex: 1000,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1 }}>
              <Box
                component="img"
                src={logo}
                alt="Heyy"
                sx={{
                  height: 40,
                  mr: 2,
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Stepper
                  activeStep={currentStep}
                  sx={{
                    '& .MuiStepLabel-root .Mui-active': {
                      color: 'black',
                    },
                    '& .MuiStepLabel-root .Mui-completed': {
                      color: 'black',
                    },
                    '& .MuiStepLabel-label': {
                      fontSize: {
                        xs: '12px',  // smaller font on mobile
                        sm: '14px'   // regular font size on larger screens
                      }
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: {
                        xs: '20px',  // smaller icons on mobile
                        sm: '24px'   // regular icon size on larger screens
                      }
                    },
                    transform: {
                      xs: 'translateX(-5%)',  // slight left shift on mobile
                      sm: 'none'              // no shift on desktop
                    }
                  }}
                  alternativeLabel  // puts labels below icons
                >
                  {['', '', '', ''].map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>

          </Box>
        )}
        <Box sx={{ mt: isOverlay ? '2em' : '7em' }}>
          {renderStep()}
        </Box>
      </Container>
      {/* Sticky Footer */}
      {currentStep < 4 ? (
        <Box
          sx={{
            position: isOverlay ? 'static' : 'fixed',
            bottom: 0,
            left: isOverlay ? undefined : 0,
            right: isOverlay ? undefined : 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            padding: '16px 0',
            zIndex: 1000,
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ display: 'flex', gap: 2}}>
              {/* Only show Back button if not on first step */}
              {currentStep > 0 && (
                <SecondaryButton
                  onClick={handleBack}
                  sx={{
                    flex: 1,
                  }}
                >
                  BACK
                </SecondaryButton>
              )}
              {renderActionButton()}
            </Box>
          </Container>
        </Box>
      ) : null}
    </ThemeProvider>
  );
};

export default ListingOnboardingFlow